/**
 * Field value formatters for debug display
 *
 * All formatters accept `unknown` to satisfy the generic DebugFieldDefinition interface.
 * Type checking is done internally for safety.
 */

/**
 * Format a number as currency
 */
export function formatCurrency(value: unknown): string {
  if (value === undefined || value === null || typeof value !== 'number') return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a number as currency with cents
 */
export function formatCurrencyPrecise(value: unknown): string {
  if (value === undefined || value === null || typeof value !== 'number') return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a number as percentage
 */
export function formatPercent(value: unknown, decimals = 2): string {
  if (value === undefined || value === null || typeof value !== 'number') return 'N/A';
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format a number with commas
 */
export function formatNumber(value: unknown, decimals = 0): string {
  if (value === undefined || value === null || typeof value !== 'number') return 'N/A';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a boolean as Yes/No
 */
export function formatBoolean(value: unknown): string {
  if (value === undefined || value === null || typeof value !== 'boolean') return 'N/A';
  return value ? 'Yes' : 'No';
}

/**
 * Format a date string
 */
export function formatDate(value: unknown): string {
  if (!value || typeof value !== 'string') return 'N/A';
  return value; // Already formatted as YYYY-MM-DD or MM/DD/YYYY
}

/**
 * Format millage rate
 */
export function formatMillage(value: unknown): string {
  if (value === undefined || value === null || typeof value !== 'number') return 'N/A';
  return `${value.toFixed(4)} mills`;
}
